import {
  AnalyticsOverviewResponse,
  AnalyticsSection,
  MetricResult,
  ANALYTICS_SECTION_LABELS,
} from '../types/adminAnalytics.ts';
import { excludeActiveUserMetrics } from './adminAnalyticsActiveUsers.ts';

export type AnalyticsChartPoint = {
  name: string;
  fullLabel: string;
  actual: number;
  anterior: number;
};

const MAX_SECTION_METRICS = 12;

export function truncateChartLabel(label: string, max = 32): string {
  if (label.length <= max) return label;
  return `${label.slice(0, max - 1)}…`;
}

export function metricsToChartData(
  metrics: MetricResult[],
  maxItems = MAX_SECTION_METRICS
): AnalyticsChartPoint[] {
  const filtered = excludeActiveUserMetrics(metrics);
  const sorted = [...filtered].sort((a, b) => b.current - a.current).slice(0, maxItems);
  return sorted.map((m) => ({
    name: truncateChartLabel(m.label),
    fullLabel: m.label,
    actual: m.current,
    anterior: m.previous,
  }));
}

export function overviewToChartData(
  overview: AnalyticsOverviewResponse,
  sectionKeys: AnalyticsSection[]
): AnalyticsChartPoint[] {
  return sectionKeys
    .map((key) => {
      const metrics = excludeActiveUserMetrics(overview.sections[key]?.metrics ?? []);
      if (!metrics.length) return null;
      const actual = metrics.reduce((sum, m) => sum + m.current, 0);
      const anterior = metrics.reduce((sum, m) => sum + m.previous, 0);
      return {
        name: truncateChartLabel(ANALYTICS_SECTION_LABELS[key], 24),
        fullLabel: ANALYTICS_SECTION_LABELS[key],
        actual,
        anterior,
      };
    })
    .filter((row): row is AnalyticsChartPoint => row !== null);
}

export function getSectionChartHeight(metricCount: number): number {
  const rows = Math.min(metricCount, MAX_SECTION_METRICS);
  return Math.min(520, Math.max(240, rows * 36 + 100));
}

export function getOverviewChartHeight(sectionCount: number): number {
  return Math.min(400, Math.max(220, sectionCount * 48 + 100));
}
